import type { SettingsInterface } from '~~/server/types/settings'

export interface MarketingProperties {
    currency?: string
    value?: number
    contents?: { id: string; quantity: number; item_price?: number }[]
    search_string?: string
}

type MarketingEvent = { event: string; event_id: string; page_url: string; occurred_at: number; properties: MarketingProperties }
type PendingEvent = { event: MarketingEvent; context: NonNullable<ReturnType<typeof marketingContext>>; targets: string; attempts: number; retry_at: number }
export type OpenAiPurchase = { pixel_id: string; event_id: string; data: Record<string, unknown> }
type OpenAiQueue = ((...args: unknown[]) => void) & { q?: unknown[][] }
type OpenAiWindow = Window & { oaiq?: OpenAiQueue }
const openAiEvents: Record<string, string> = {
    PageView: 'page_viewed', ViewContent: 'contents_viewed', AddToCart: 'items_added',
    InitiateCheckout: 'checkout_started', Purchase: 'order_created', Lead: 'lead_created', CompleteRegistration: 'registration_completed',
    Subscribe: 'subscription_created', Schedule: 'appointment_scheduled',
}
type TikTokQueue = unknown[][] & Record<string, unknown> & {
    track: (event: string, properties: Record<string, unknown>, options: { event_id: string }) => void
    page: () => void
    grantConsent: () => void
    revokeConsent: () => void
}
type TikTokWindow = Window & { ttq?: TikTokQueue; TiktokAnalyticsObject?: string }
type TrackingOptions = {
    settings: () => SettingsInterface
    analyticsAllowed?: () => boolean
    allowed: () => boolean
    send: (body: Record<string, unknown>) => Promise<unknown>
}

let options: TrackingOptions | undefined
let pending: PendingEvent[] = []
let inFlight: Promise<void> | undefined
const outboxKey = 'marketing:outbox:v1'
const maxOutbox = 100
const retention = 24 * 60 * 60 * 1000
const targetKey = () => JSON.stringify(['facebook', 'tiktok', 'openai'].map(provider => {
    const settings = options!.settings() as unknown as Record<string, unknown>
    return [provider, settings[`${provider}_pixel`], settings[`${provider}_events_api_enable`]]
}))
const persistPending = () => {
    try { if (pending.length) localStorage.setItem(outboxKey, JSON.stringify(pending)); else localStorage.removeItem(outboxKey) } catch { /* The in-memory queue remains available. */ }
}
const restorePending = () => {
    try {
        const saved: PendingEvent[] = JSON.parse(localStorage.getItem(outboxKey) || '[]')
        if (Array.isArray(saved)) pending = saved.filter(item => item?.event?.occurred_at > Date.now() - retention
            && item.event.occurred_at <= Date.now() && item.context?.consent_id === consentId
            && item.targets === targetKey()).slice(-maxOutbox)
    } catch { pending = [] }
}
const scheduleFlush = (delay = 1500) => {
    if (timer) return
    timer = setTimeout(() => { timer = undefined; void flushMarketingEvents() }, delay)
}
let timer: ReturnType<typeof setTimeout> | undefined
let pixelLoaded = false
let pixelScheduled = false
let landingClick: { ttclid?: string; fbclid?: string; oppref?: string; time: number } = { time: 0 }
let consentId: string | undefined
let wasAllowed = false
let listenersBound = false
let openAiScriptLoaded = false
let openAiPage = ''
const openAiPixels = new Map<string, boolean>()
const openAiPurchases = new Set<string>()
const consentCookie = 'webixty_marketing_consent'
const openAiActive = () => !!options?.settings().openai_pixel
    && !!(options.settings().openai_pixel_enable || options.settings().openai_events_api_enable)
const referenceCookie = (name: string, maxLength: number) => {
    const value = cookie(name)
    return value && value.trim() && value.length <= maxLength ? value : undefined
}

const cookie = (name: string) => {
    const raw = document.cookie.split('; ').find(value => value.startsWith(`${name}=`))?.slice(name.length + 1)
    try { return raw ? decodeURIComponent(raw) : undefined } catch { return undefined }
}
const saveCookie = (name: string, value: string, maxAge = 60 * 60 * 24 * 90) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`
}
const pageUrl = () => `${location.origin}${location.pathname}`

export function analyticsContext() {
    if (typeof window === 'undefined' || !options?.analyticsAllowed?.() || !options.settings().ga) return undefined
    const id = cookie('webixty_analytics_consent')
    const clientId = cookie('webixty_analytics_client')
    if (!id || !clientId) return undefined
    return { consent: true, consent_id: id, client_id: clientId, page_url: pageUrl(), ad_consent: options.allowed() }
}

export function updateAnalyticsConsent() {
    if (typeof window === 'undefined' || !options) return
    const id = cookie('webixty_analytics_consent')
    if (!options.analyticsAllowed?.() || !options.settings().ga) {
        if (id) revokeMarketingConsent(id)
        saveCookie('webixty_analytics_consent', '', 0)
        saveCookie('webixty_analytics_client', '', 0)
        return
    }
    if (!id) saveCookie('webixty_analytics_consent', crypto.randomUUID(), 60 * 60 * 24 * 365)
    if (!cookie('webixty_analytics_client')) {
        const existing = cookie('_ga')?.match(/^GA\d+\.\d+\.(\d+\.\d+)$/)?.[1]
        saveCookie('webixty_analytics_client', existing || crypto.randomUUID(), 60 * 60 * 24 * 365)
    }
}

export function marketingContext() {
    if (typeof window === 'undefined' || !options?.allowed()) return undefined
    updateMarketingAttribution()
    return {
        consent: true,
        consent_id: consentId,
        page_url: pageUrl(),
        fbp: metaCookie('_fbp'),
        fbc: metaCookie('_fbc'),
        ttp: referenceCookie('_ttp', 255),
        ttclid: referenceCookie('webixty_ttclid', 1024),
        ...(openAiActive() ? { oppref: referenceCookie('__oppref', 4096), obref: referenceCookie('__obref', 1024) } : {}),
    }
}

function openAiQueue(): OpenAiQueue {
    const browser = window as OpenAiWindow
    if (browser.oaiq) return browser.oaiq
    const queue: OpenAiQueue = (...args) => { queue.q!.push(args) }
    queue.q = []
    browser.oaiq = queue
    return queue
}

function updateOpenAiConsent(allowed: boolean) {
    const settings = options!.settings()
    const enabled = allowed && !!settings.openai_pixel_enable && !!settings.openai_pixel
    const existing = (window as OpenAiWindow).oaiq
    if (!enabled) {
        if (existing?.q) existing.q = existing.q.filter(command => command[0] !== 'measure' && command[0] !== 'measureSingle')
        if (!allowed || !openAiActive()) existing?.('consent', false)
        if (!allowed || !openAiActive()) {
            saveCookie('__oppref', '', 0)
            saveCookie('__obref', '', 0)
        }
        openAiPage = ''
        return
    }

    const queue = openAiQueue()
    const id = settings.openai_pixel!
    const debug = !!settings.openai_pixel_debug
    if (openAiPixels.get(id) !== debug) {
        queue('consent', false)
        queue('init', { pixelId: id, debug })
        openAiPixels.set(id, debug)
    }
    queue('consent', true)
    if (openAiScriptLoaded || document.querySelector?.('script[src="https://bzrcdn.openai.com/sdk/oaiq.min.js"]')) return
    const script = document.createElement('script')
    script.async = true
    script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js'
    script.onerror = () => { openAiScriptLoaded = false; script.remove() }
    document.head.appendChild(script)
    openAiScriptLoaded = true
}

export function updateMarketingAttribution() {
    if (typeof window === 'undefined' || !options?.allowed()) return
    const query = new URLSearchParams(location.search)
    const ttclid = query.get('ttclid') || landingClick.ttclid
    if (ttclid && ttclid.length <= 1024 && cookie('webixty_ttclid') !== ttclid) saveCookie('webixty_ttclid', ttclid)
    const fbclid = query.get('fbclid') || landingClick.fbclid
    if (fbclid && /^[A-Za-z0-9_.-]+$/.test(fbclid)) {
        const time = landingClick.fbclid === fbclid ? landingClick.time : Date.now()
        const fbc = `fb.1.${time}.${fbclid}`
        if (fbc.length <= 1024 && metaCookie('_fbc')?.split('.').slice(3).join('.') !== fbclid) saveCookie('_fbc', fbc)
    }
    if (openAiActive()) {
        const reference = query.get('oppref') || landingClick.oppref
        if (reference && reference.length <= 4096 && cookie('__oppref') !== reference) saveCookie('__oppref', reference, 60 * 60 * 24 * 30)
    }
    landingClick = { time: 0 }
}

function metaCookie(name: '_fbp' | '_fbc') {
    const value = referenceCookie(name, name === '_fbp' ? 255 : 1024)
    const pattern = name === '_fbp' ? /^fb\.\d+\.\d+\.[A-Za-z0-9_-]+$/ : /^fb\.\d+\.\d+\.[A-Za-z0-9_.-]+$/
    return value && pattern.test(value) ? value : undefined
}

function openAiData(properties: MarketingProperties): Record<string, unknown> {
    const data: Record<string, unknown> = { type: 'contents' }
    const currency = properties.currency
    let factor = 100
    if (currency && /^[A-Z]{3}$/.test(currency)) {
        factor = 10 ** new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits!
        if (properties.value !== undefined) Object.assign(data, { amount: Math.round(properties.value * factor), currency })
    }
    if (properties.contents?.length) data.contents = properties.contents.map(item => ({
        id: item.id, content_type: 'product',
        ...(Number.isInteger(item.quantity) && item.quantity > 0 ? { quantity: item.quantity } : {}),
        ...(currency && item.item_price !== undefined ? { amount: Math.round(item.item_price * factor), currency } : {}),
    }))
    return data
}

function measureOpenAi(event: string, properties: MarketingProperties, eventId: string, purchase?: OpenAiPurchase | null) {
    const settings = options!.settings()
    if (!settings.openai_pixel_enable || !settings.openai_pixel || !openAiEvents[event]) return
    if (event === 'Purchase' && (!purchase || purchase.pixel_id !== settings.openai_pixel || purchase.event_id !== eventId)) return
    const key = `openai:purchase:${settings.openai_pixel}:${eventId}`
    if (event === 'Purchase') {
        if (openAiPurchases.has(key)) return
        try { if (localStorage.getItem(key)) return } catch { /* In-memory and provider deduplication remain available. */ }
    }
    updateOpenAiConsent(true)
    openAiQueue()('measureSingle', settings.openai_pixel, openAiEvents[event], purchase?.data ?? (['Lead', 'CompleteRegistration', 'Schedule'].includes(event) ? { type: 'customer_action' } : event === 'Subscribe' ? { ...openAiData(properties), type: 'plan_enrollment' } : openAiData(properties)), { event_id: eventId })
    if (event === 'Purchase') {
        openAiPurchases.add(key)
        try { localStorage.setItem(key, '1') } catch { /* Storage may be unavailable. */ }
    }
}

export function trackMarketingPageView() {
    if (typeof window === 'undefined' || !options?.allowed()) return
    updateMarketingAttribution()
    if (!openAiActive()) return
    const key = `${options.settings().openai_pixel}:${pageUrl()}`
    if (key === openAiPage) return
    openAiPage = key
    trackMarketing('PageView', {})
}

function retryConsentRevocations() {
    if (!options) return
    let ids: string[] = []
    try { ids = JSON.parse(localStorage.getItem('marketing:revocations') || '[]') } catch { /* Storage may be unavailable. */ }
    for (const id of ids) void options.send({ events: [], context: { consent: false, consent_id: id, page_url: pageUrl() } }).then(() => {
        try {
            const current: string[] = JSON.parse(localStorage.getItem('marketing:revocations') || '[]')
            localStorage.setItem('marketing:revocations', JSON.stringify(current.filter(value => value !== id)))
        } catch { /* Storage may be unavailable. */ }
    }).catch(() => { /* Retry the privacy request on the next consent update or page exit. */ })
}

function revokeMarketingConsent(id: string) {
    try {
        const ids: string[] = JSON.parse(localStorage.getItem('marketing:revocations') || '[]')
        localStorage.setItem('marketing:revocations', JSON.stringify([...new Set([...ids, id])]))
        retryConsentRevocations()
    } catch {
        void options!.send({ events: [], context: { consent: false, consent_id: id, page_url: pageUrl() } }).catch(() => {})
    }
}

function tikTokQueue(): TikTokQueue {
    const browser = window as TikTokWindow
    if (browser.ttq) return browser.ttq
    const queue = [] as unknown as TikTokQueue
    browser.TiktokAnalyticsObject = 'ttq'
    browser.ttq = queue
    const methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie', 'holdConsent', 'revokeConsent', 'grantConsent']
    const defer = (target: Record<string, unknown> & { push: (value: unknown[]) => unknown }, method: string) => {
        target[method] = (...args: unknown[]) => target.push([method, ...args])
    }
    queue.methods = methods
    queue.setAndDefer = defer
    for (const method of methods) defer(queue, method)
    queue.instance = (id: string) => {
        const instances = queue._i as Record<string, unknown[][]>
        const instance = (instances?.[id] || []) as unknown as TikTokQueue
        for (const method of methods) defer(instance, method)
        return instance
    }
    return queue
}

export function updateMarketingConsent() {
    if (typeof window === 'undefined' || !options) return
    const allowed = options.allowed()
    const savedConsentId = cookie(consentCookie)
    consentId ??= savedConsentId && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(savedConsentId) ? savedConsentId : undefined
    updateOpenAiConsent(allowed)
    const queue = (window as TikTokWindow).ttq
    if (!allowed) {
        pending = []
        persistPending()
        clearTimeout(timer)
        timer = undefined
        queue?.revokeConsent()
        saveCookie('webixty_ttclid', '', 0)
        if (consentId) revokeMarketingConsent(consentId)
        saveCookie(consentCookie, '', 0)
        consentId = undefined
        if (wasAllowed) landingClick = { time: 0 }
        wasAllowed = false
        return
    }

    consentId ??= crypto.randomUUID()
    if (cookie(consentCookie) !== consentId) saveCookie(consentCookie, consentId, 60 * 60 * 24 * 365)
    if (!wasAllowed) restorePending()
    wasAllowed = true
    if (pending.length) scheduleFlush()
    retryConsentRevocations()
    updateMarketingAttribution()
    trackMarketingPageView()

    const id = options.settings().tiktok_pixel
    if (!id) return
    tikTokQueue().grantConsent()
    if (pixelLoaded || pixelScheduled) return
    pixelScheduled = true
    const load = () => {
        pixelScheduled = false
        if (!options?.allowed() || pixelLoaded) return
        const ttq = tikTokQueue()
        const src = 'https://analytics.tiktok.com/i18n/pixel/events.js'
        const instance = [] as unknown as TikTokQueue
        instance._u = src
        ttq._i = { [id]: instance }
        ttq._t = { [id]: Date.now() }
        ttq._o = { [id]: {} }
        const script = document.createElement('script')
        script.async = true
        script.fetchPriority = 'low'
        script.src = `${src}?sdkid=${encodeURIComponent(id)}&lib=ttq`
        document.head.appendChild(script)
        pixelLoaded = true
        ttq.page()
    }
    if ('requestIdleCallback' in window) window.requestIdleCallback(load, { timeout: 1500 })
    else setTimeout(load, 0)
}

export function configureMarketingTracking(value: TrackingOptions) {
    options = value
    if (typeof window === 'undefined') return
    const query = new URLSearchParams(location.search)
    landingClick = { ttclid: query.get('ttclid') || undefined, fbclid: query.get('fbclid') || undefined, oppref: query.get('oppref') || undefined, time: Date.now() }
    if (listenersBound) return
    listenersBound = true
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') { void flushMarketingEvents(); retryConsentRevocations() }
    })
    window.addEventListener('pagehide', () => { void flushMarketingEvents(true); retryConsentRevocations() })
    window.addEventListener('online', () => { void flushMarketingEvents() })
}

export function trackMarketing(event: string, properties: MarketingProperties, eventId?: string, purchase?: OpenAiPurchase | null, onlyProvider?: 'tiktok' | 'openai'): string | undefined {
    if (typeof window === 'undefined' || !options?.allowed()) return undefined
    const id = eventId || crypto.randomUUID()
    const settings = options.settings()
    if (!onlyProvider || onlyProvider === 'openai') measureOpenAi(event, properties, id, purchase)
    if ((!onlyProvider || onlyProvider === 'tiktok') && event !== 'PageView' && settings.tiktok_pixel) {
        const { search_string, ...pixelProperties } = properties
        tikTokQueue().track(event === 'Lead' ? 'SubmitForm' : event, {
            ...pixelProperties,
            ...(search_string !== undefined ? { query: search_string } : {}),
            content_type: properties.contents?.length ? 'product' : undefined,
            contents: properties.contents?.map(item => ({ content_id: item.id, quantity: item.quantity, price: item.item_price })),
        }, { event_id: id })
    }
    const serverEnabled = event === 'PageView' ? settings.openai_events_api_enable
        : settings.facebook_events_api_enable || settings.tiktok_events_api_enable || (settings.openai_events_api_enable && openAiEvents[event])
    if (event !== 'Purchase' && serverEnabled) {
        const context = marketingContext()!
        pending.push({ event: { event, event_id: id, page_url: pageUrl(), occurred_at: Date.now(), properties: structuredClone(properties) }, context, targets: targetKey(), attempts: 0, retry_at: 0 })
        if (pending.length > maxOutbox) pending.splice(0, pending.length - maxOutbox)
        persistPending()
        if (pending.length >= 10) void flushMarketingEvents()
        else scheduleFlush()
    }
    return id
}

export async function flushMarketingEvents(exiting = false): Promise<void> {
    clearTimeout(timer)
    timer = undefined
    if (!options?.allowed()) { pending = []; persistPending(); return }
    if (inFlight) {
        await inFlight
        if (exiting && pending.some(item => item.retry_at <= Date.now())) await flushMarketingEvents(true)
        return
    }
    pending = pending.filter(item => item.event.occurred_at > Date.now() - retention && item.targets === targetKey() && item.context.consent_id === consentId)
    const first = pending.find(item => item.retry_at <= Date.now())
    if (!first) {
        if (pending.length) scheduleFlush(Math.max(1000, Math.min(...pending.map(item => item.retry_at)) - Date.now()))
        persistPending()
        return
    }
    const batch = pending.filter(item => item.targets === first.targets && JSON.stringify(item.context) === JSON.stringify(first.context)
        && item.retry_at <= Date.now()).slice(0, 10)
    inFlight = (async () => {
        try {
            await Promise.resolve()
            await options!.send({ events: batch.map(item => item.event), context: first.context })
            pending = pending.filter(item => !batch.includes(item))
        } catch (error) {
            const status = Number((error as { statusCode?: number; status?: number })?.statusCode ?? (error as { status?: number })?.status)
            if (status >= 400 && status < 500 && status !== 408 && status !== 429) pending = pending.filter(item => !batch.includes(item))
            else for (const item of batch) {
                item.attempts++
                item.retry_at = Date.now() + Math.min(60000, 2000 * 2 ** Math.min(item.attempts, 5))
            }
        } finally {
            persistPending()
            inFlight = undefined
            if (pending.length && options?.allowed() && !exiting) scheduleFlush(Math.max(1000, Math.min(...pending.map(item => item.retry_at)) - Date.now()))
        }
    })()
    await inFlight
    if (exiting && pending.some(item => item.retry_at <= Date.now())) await flushMarketingEvents(true)
}
